import { z } from 'zod';
import { apiClient, ApiErrorException } from '../client';

/**
 * W6 back office: the audit log (GET /api/admin/audit-logs, facets, CSV
 * export) and the broadcast notifications (/api/admin/notifications).
 * Super admin only on the server.
 */

// ── Audit log ────────────────────────────────────────────────────────────

const AuditLogItemSchema = z.object({
  id: z.string(),
  action: z.string(),
  actor_user_id: z.string(),
  actor_email: z.string(),
  actor_organisation_id: z.string(),
  target_type: z.string(),
  target_id: z.string(),
  target_label: z.string(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  ip: z.string(),
  user_agent: z.string(),
  created_date: z.string(),
});

export type AuditLogItem = z.infer<typeof AuditLogItemSchema>;

export interface AuditLogFilters {
  action?: string;
  /** Part of the actor's email. */
  actor?: string;
  organisation_id?: string;
  /** ISO, inclusive. */
  from?: string;
  /** ISO, exclusive. */
  to?: string;
}

const PageSchema = <T extends z.ZodTypeAny>(item: T) => z.object({ items: z.array(item), next_cursor: z.string().nullable(), total: z.number().int() });

export type CursorPage<T> = { items: T[]; next_cursor: string | null; total: number };

function filterQuery(filters: AuditLogFilters): Record<string, string | undefined> {
  return {
    action: filters.action || undefined,
    actor: filters.actor?.trim() || undefined,
    organisation_id: filters.organisation_id || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
}

export async function listAuditLogPage(filters: AuditLogFilters, page: { limit: number; cursor?: string }, signal?: AbortSignal): Promise<CursorPage<AuditLogItem>> {
  const raw = await apiClient.get<unknown>('/admin/audit-logs', {
    signal,
    query: { ...filterQuery(filters), limit: page.limit, cursor: page.cursor },
  });
  return PageSchema(AuditLogItemSchema).parse(raw);
}

export async function listAuditLogActions(signal?: AbortSignal): Promise<Array<{ action: string; count: number }>> {
  const raw = await apiClient.get<unknown>('/admin/audit-logs/facets', { signal });
  return z.object({ actions: z.array(z.object({ action: z.string(), count: z.number() })) }).parse(raw).actions;
}

const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/**
 * Downloads the filtered audit log as CSV (server export, SR3-5: the only one).
 * Returns whether the server truncated it (more than 10 000 rows).
 */
export async function downloadAuditLogCsv(filters: AuditLogFilters): Promise<{ truncated: boolean }> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filterQuery(filters))) if (v) qs.set(k, v);
  const resp = await fetch(`${API_BASE}/admin/audit-logs/export${qs.toString() ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!resp.ok) {
    let body: { error?: { code?: string; message?: string } } = {};
    try { body = await resp.json(); } catch { /* not JSON */ }
    throw new ApiErrorException({ code: body.error?.code ?? 'EXPORT_FAILED', message: body.error?.message ?? resp.statusText }, resp.status);
  }
  const blob = await resp.blob();
  const name = /filename="([^"]+)"/.exec(resp.headers.get('Content-Disposition') ?? '')?.[1] ?? 'audit-log.csv';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return { truncated: resp.headers.get('X-Export-Truncated') === 'true' };
}

// ── Broadcast notifications ──────────────────────────────────────────────

export const BROADCAST_TYPES = ['info', 'promo', 'reminder', 'update'] as const;
export type BroadcastType = (typeof BROADCAST_TYPES)[number];

const BroadcastSchema = z.object({
  id: z.string(),
  title: z.string(),
  message: z.string(),
  type: z.enum(BROADCAST_TYPES),
  target_type: z.enum(['all', 'specific']),
  organisation_ids: z.array(z.string()),
  recipients: z.number().int(),
  read_count: z.number().int(),
  send_email: z.boolean(),
  email_status: z.enum(['none', 'sending', 'sent', 'partial', 'failed', 'unavailable']),
  emails_sent: z.number().int(),
  emails_failed: z.number().int(),
  created_by_email: z.string(),
  created_date: z.string(),
});

export type Broadcast = z.infer<typeof BroadcastSchema>;

export interface BroadcastInput {
  title: string;
  message: string;
  type: BroadcastType;
  target_type: 'all' | 'specific';
  organisation_ids?: string[];
  send_email: boolean;
}

export async function listBroadcastPage(page: { limit: number; cursor?: string }, signal?: AbortSignal): Promise<CursorPage<Broadcast>> {
  const raw = await apiClient.get<unknown>('/admin/notifications', { signal, query: { limit: page.limit, cursor: page.cursor } });
  return PageSchema(BroadcastSchema).parse(raw);
}

export async function sendBroadcast(input: BroadcastInput): Promise<Broadcast> {
  const raw = await apiClient.post<unknown>('/admin/notifications', input);
  return z.object({ broadcast: BroadcastSchema }).parse(raw).broadcast;
}

export async function deleteBroadcast(id: string): Promise<void> {
  await apiClient.delete<unknown>(`/admin/notifications/${encodeURIComponent(id)}`);
}
